import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Chip,
  Tooltip,
  Divider,
  useTheme
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import FeatherIcon from 'feather-icons-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Componente de tarjeta para mostrar un tablero Kanban.
 *
 * Implementa el principio de responsabilidad única (SRP) de SOLID al encargarse
 * exclusivamente de la presentación visual de un tablero Kanban.
 *
 * @param {Object} board - Objeto con la información del tablero a mostrar
 * @param {Function} onTogglePinned - Función para alternar el estado de fijación del tablero
 * @param {Boolean} isPinned - Estado actual de fijación del tablero
 * @param {Function} onViewDetails - Función para mostrar los detalles del tablero
 * @returns {JSX.Element} Componente de tarjeta con la información del tablero
 */
const BoardCard = ({
  board,
  onTogglePinned,
  isPinned,
  onViewDetails
}) => {
  const theme = useTheme();

  // Formatear la fecha de última actualización si existe, utilizando el formato localizado
  const formattedDate = board.updated_at
    ? format(parseISO(board.updated_at), "dd MMM yyyy", { locale: es })
    : 'Sin actualizar';


  // Procesar metadatos personalizados para mostrar información adicional
  const metaData = {};
  if (board.meta_data) {
    try {
      // Convertir meta_data a objeto si viene como string JSON
      const parsedMetaData = typeof board.meta_data === 'string'
        ? JSON.parse(board.meta_data)
        : board.meta_data;

      // Filtrar campos relevantes para mostrar, excluyendo campos internos
      Object.keys(parsedMetaData).forEach(key => {
        // Excluir campos internos del sistema que no son relevantes para el usuario
        if (key !== 'agente' && key !== 'fecha_creacion') {
          metaData[key] = parsedMetaData[key];
        }
      });
    } catch (error) {
      // Error silencioso
    }
  }

  /**
   * Maneja el evento de clic en la tarjeta
   * Delega la acción a la función proporcionada por el componente padre
   */
  const handleCardClick = () => {
    if (onViewDetails) {
      onViewDetails(board);
    }
  };

  // Obtener la descripción del tablero, priorizando el campo principal sobre los metadatos
  const description = board.description || (metaData.descripcion ? metaData.descripcion : '');

  // Verificar si existen metadatos personalizados para mostrar (excluyendo descripción)
  const hasCustomMetadata = Object.keys(metaData).length > 0 &&
                          (Object.keys(metaData).filter(key => key !== 'descripcion').length > 0);

  return (
    <Card
      sx={{
        maxWidth: '100%',
        mb: 2,
        borderRadius: 2,
        transition: 'transform 0.3s, box-shadow 0.3s',
        position: 'relative',
        overflow: 'visible',
        border: `1px solid ${theme.palette.grey[200]}`,
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 8px 25px 0 rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* Botón para fijar/desfijar el tablero */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          // Prevenir la propagación del evento para evitar abrir el tablero
          e.preventDefault();
          // Llamar a la función para alternar el estado de fijado
          if (typeof onTogglePinned === 'function') {
      
            onTogglePinned(board.id);
          } else {
            console.error('La función onTogglePinned no está definida');
          }
        }}
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 16,
          zIndex: 1,
          // Mejorar la visibilidad y feedback del botón
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.95)',
          }
        }}
        aria-label={isPinned ? "Quitar de favoritos" : "Agregar a favoritos"}
      >
        {isPinned ?
          <StarIcon color="warning" /> :
          <StarBorderIcon />
        }
      </IconButton>

      {/* Área principal clickeable que muestra la información del tablero */}
      <CardActionArea onClick={handleCardClick}>
        <CardContent sx={{ pt: 3, px: 3 }}>
          {/* Título del tablero con fallback a valores alternativos */}
          <Typography
            variant="h5"
            component="div"
            sx={{
              mb: 2,
              mt: 1,
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            {board.name || board.title || 'Sin título'}
          </Typography>

          {/* Descripción del tablero (si existe) */}
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2.5,
                lineHeight: 1.6
              }}
            >
              {description}
            </Typography>
          )}

          {/* Sección de metadatos personalizados con formato de chips */}
          {hasCustomMetadata && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 1,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                  letterSpacing: '0.5px'
                }}
              >
                Información adicional
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.8}>
                {Object.entries(metaData).map(([key, value], index) => (
                  key !== 'descripcion' && (
                    <Chip
                      key={index}
                      label={
                        <Box>
                          <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                            {key}:
                          </Typography>
                          <Typography component="span" sx={{ fontSize: '0.7rem', ml: 0.5 }}>
                            {value}
                          </Typography>
                        </Box>
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        height: 'auto',
                        minHeight: '24px',
                        py: 0.3,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.grey[300]
                      }}
                    />
                  )
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </CardActionArea>

      <Divider />

      <CardActions
        sx={{
          justifyContent: 'flex-end',
          px: 3,
          py: 1.5,
          backgroundColor: theme.palette.grey[50]
        }}
      >
        <Tooltip title={board.updated_at ? `Actualizado el ${formattedDate}` : 'Sin actualizar'}>
          <Box display="flex" alignItems="center">
            <FeatherIcon icon="calendar" size={14} style={{ color: theme.palette.text.secondary }} />
            <Typography
              variant="body2"
              sx={{
                ml: 0.5,
                fontSize: '0.75rem',
                color: theme.palette.text.secondary
              }}
            >
              {formattedDate}
            </Typography>
          </Box>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default BoardCard;